import { useState } from 'react'
import { CheckSquare, Copy, Gavel, HelpCircle, ListChecks, Quote, Sparkles } from 'lucide-react'
import { Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Tooltip } from '@/components/ui'
import { AttributionNotice, SpeakerChip } from '@/components/domain'
import { PlayerTimecode } from '@/features/player'
import { useT } from '@/internationalization/i18n'
import { toast } from '@/stores'
import type { MeetingDetail } from '@/types'
import { SummarySections } from './SummarySections'
import { sectionsCopyLines, summaryLead, topicSections } from './sections'

/**
 * Narrative, key points, decisions and next steps for one meeting.
 *
 * Two product rules are enforced visually here rather than only in the backend:
 *
 *  1. A decision shows its verbatim quote on demand, and the quote is NEVER
 *     translated — it is the only unedited evidence that the decision was made,
 *     and translating it destroys the one thing it is for.
 *  2. An unowned action item is shown and labelled "Unassigned", never hidden.
 *     A silently dropped item looks exactly like a meeting where nobody
 *     committed to anything.
 */
export function SummaryPanel({
  meeting,
  onOpenTranscriptAt,
}: {
  meeting: MeetingDetail
  /** Where a timecode goes when the recording cannot play. */
  onOpenTranscriptAt?: (seconds: number) => void
}) {
  const t = useT()
  const [openQuote, setOpenQuote] = useState<string | null>(null)

  if (!meeting.summary) {
    return (
      <Card>
        <EmptyState
          icon={<Sparkles />}
          title={meeting.status === 'processing' ? t('summary.beingGeneratedTitle') : t('summary.noSummaryTitle')}
          description={
            meeting.status === 'processing'
              ? t('summary.beingGeneratedBody')
              : t('summary.noSummaryBody')
          }
        />
      </Card>
    )
  }

  const summary = meeting.summary
  const sections = topicSections(summary)
  const lead = summaryLead(summary)
  const copyAll = async () => {
    const text = [
      meeting.title,
      '',
      lead,
      '',
      // Absent sections add nothing at all -- not a heading over nothing -- so
      // a summary written without them copies exactly as it always did.
      ...(sections.length > 0 ? [t('summary.copy.sectionsHeading'), ...sectionsCopyLines(sections)] : []),
      // THE HEADING CARRIES THE PROVENANCE, because this is the one artefact
      // that LEAVES the product. Pasted into an email or a ticket, a derived
      // list with a plain "Key points:" over it presents a rule's fallback as
      // the summariser's considered answer -- exactly what the card on screen
      // is built to prevent.
      ...(summary.keyPoints.length > 0
        ? [summary.keyPointsDerived
            ? t('summary.copy.keyPointsDerivedHeading')
            : t('summary.copy.keyPointsHeading'),
           ...summary.keyPoints.map((p) => `- ${p.text}`), '']
        : []),
      t('summary.copy.decisionsHeading'),
      ...summary.decisions.map((d) => `- ${d.text}${d.decidedBy ? ` (${d.decidedBy})` : ''}`),
      '',
      t('summary.copy.actionItemsHeading'),
      ...meeting.actionItems.map((a) => `- ${a.text} — ${a.owner?.displayName ?? t('summary.unassigned')}`),
    ].join('\n')
    await navigator.clipboard.writeText(text)
    toast.success(t('summary.copied'))
  }

  return (
    <div className="space-y-4">
      <AttributionNotice check={meeting.clockCheck} />

      {summary.devanagariFieldsRemaining > 0 && (
        <Alert tone="warning" title={t('summary.notInEnglishTitle')}>
          {t('summary.fieldsUntranslated', { count: summary.devanagariFieldsRemaining })}
        </Alert>
      )}

      <Card>
        <CardHeader
          title={t('summary.discussion')}
          description={t('summary.generatedPrompt', {
            when: new Date(summary.generatedAt).toLocaleString(),
            version: summary.promptVersion,
          })}
          action={
            <div className="flex items-center gap-2">
              <Button size="sm" leftIcon={<Copy className="h-4 w-4" />} onClick={copyAll}>
                {t('summary.copy')}
              </Button>
            </div>
          }
        />
        <CardBody>
          {summary.headline && (
            <p className="mb-3 text-md font-medium leading-snug text-fg">{summary.headline}</p>
          )}
          <p className="whitespace-pre-line text-base leading-relaxed text-fg">{lead}</p>
          {/* With sections the paragraph above is the overview and each topic
              follows; without them this renders nothing and the narrative is
              the whole discussion, as it always was. */}
          <SummarySections sections={sections} />
          {summary.topics.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {summary.topics.map((topic) => (
                <Badge key={topic} tone="neutral">
                  {topic}
                </Badge>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          {/*
            THE PROVENANCE IS PART OF THE CONTENT HERE. `keyPointsDerived`
            says these are the narrative's own leading sentences, picked by a
            rule, rather than the summariser's answer to "what must somebody
            know" — true of every meeting summarised before the m8e prompt.
            Rendering the two identically would present a fallback as a
            considered list, so the card says which it is showing.
          */}
          <CardHeader
            title={t('summary.keyPoints')}
            description={
              summary.keyPointsDerived ? t('summary.keyPointsDerivedDesc') : undefined
            }
            action={
              summary.keyPointsDerived ? (
                <Badge tone="neutral">{t('summary.keyPointsDerivedBadge')}</Badge>
              ) : undefined
            }
          />
          <CardBody>
            {summary.keyPoints.length === 0 ? (
              // An empty <ul> was what shipped, which renders as a heading over
              // nothing — indistinguishable from a card that failed to load.
              <EmptyState
                icon={<ListChecks />}
                title={t('summary.noKeyPointsTitle')}
                description={t('summary.noKeyPointsBody')}
              />
            ) : (
              <ul className="space-y-2.5">
                {summary.keyPoints.map((point) => (
                  <li key={point.id} className="flex gap-2.5 text-base text-fg">
                    <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                    {point.text}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('summary.decisions')}
            description={t('summary.decisionsDesc')}
            action={<Badge tone="accent" icon={<Gavel />}>{summary.decisions.length}</Badge>}
          />
          <CardBody className="space-y-3">
            {summary.decisions.map((decision) => (
              <div key={decision.id} className="rounded-md border border-border bg-surface-muted/40 p-3">
                <p className="text-base font-medium text-fg">{decision.text}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                  {decision.decidedBy ? (
                    <SpeakerChip name={decision.decidedBy} />
                  ) : (
                    <span className="italic text-fg-subtle">{t('summary.deciderNotIdentified')}</span>
                  )}
                  <PlayerTimecode seconds={decision.atSeconds} onUnavailable={onOpenTranscriptAt} />
                  {decision.quote && (
                    <button
                      type="button"
                      onClick={() => setOpenQuote(openQuote === decision.id ? null : decision.id)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
                    >
                      <Quote aria-hidden className="h-3.5 w-3.5" />
                      {openQuote === decision.id ? t('summary.hideQuote') : t('summary.showQuote')}
                    </button>
                  )}
                </div>
                {openQuote === decision.id && decision.quote && (
                  <Tooltip content={t('summary.quoteTip')}>
                    <blockquote className="mt-2 border-l-2 border-accent pl-3 text-base italic text-fg-muted">
                      “{decision.quote}”
                    </blockquote>
                  </Tooltip>
                )}
              </div>
            ))}
          </CardBody>
        </Card>
      </div>

      {summary.openQuestions.length > 0 && (
        <Card>
          <CardHeader
            title={t('summary.openQuestions')}
            description={t('summary.openQuestionsDesc')}
          />
          <CardBody>
            <ul className="space-y-2.5">
              {summary.openQuestions.map((question) => (
                <li key={question} className="flex gap-2.5 text-base text-fg">
                  <HelpCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  {question}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title={t('summary.actionItems')}
          action={<Badge tone="brand" icon={<CheckSquare />}>{meeting.actionItems.length}</Badge>}
        />
        <CardBody>
          <ul className="divide-y divide-border">
            {meeting.actionItems.map((item) => (
              <li key={item.id} className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:gap-3">
                <span className="min-w-0 flex-1 text-base text-fg">{item.text}</span>
                <span className="flex shrink-0 items-center gap-2">
                  {item.owner ? (
                    <Badge tone={item.ownerInferred ? 'warning' : 'neutral'}>
                      {item.owner.displayName}
                      {item.ownerInferred && t('summary.inferredSuffix')}
                    </Badge>
                  ) : (
                    <Badge tone="warning">{t('summary.unassigned')}</Badge>
                  )}
                  <PlayerTimecode seconds={item.atSeconds} onUnavailable={onOpenTranscriptAt} />
                </span>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  )
}
